import * as React from "react";
import cookie from "react-cookies";
import { BrowserRouter as Router, Redirect, Route, Switch } from "react-router-dom";
import { io, Socket } from "socket.io-client";
import { Routes } from "../../base/routes";
import { AppBarWrapped } from "../../components/AppBar";
import { SideMenu } from "../../components/SideMenu";
import { SessionUser } from "../../components/User/User";
import { Config } from "../../config/config";
import { Helpers } from "../../utils/helpers";
import { Dashboard } from "../Dashboard";
import { Error404Page } from "../ErrorPage";

interface LayoutProps {
    resetAppState(): void;
    switchMode(): void;
    activeUser: any;
    classes: any;
    mode: string;
}

interface LayoutState {
    homepageRedirect: boolean;
    activeEnv: string;
    drawerOpen: boolean;
    currentTarget: any;
    isMenuOpen: boolean;
    connectionEstablished: boolean;
    connectionActive: boolean;
}

export interface IRouteProps {
    activeUser: any;
    activeEnv: string;
    socket: Socket | undefined;
    classes: any;
}

export interface DefaultProps {
    activeUser: SessionUser;
    activeEnv: string;
    classes: any;
    form: any;
    socket: Socket | undefined,
    mode: "light" | "dark";
    match?: any;
    resetAppState: () => void;
    switchMode: (theme?: "light" | "dark") => void;
}

export class LayoutWrapper extends React.Component<LayoutProps, LayoutState> {
    private socket: Socket | undefined;

    constructor(props) {
        super(props);

        this.state = {
            connectionEstablished: false,
            connectionActive: false,
            homepageRedirect: false,
            activeEnv: cookie.load("active-env") || "stage",
            drawerOpen: true,
            currentTarget: null,
            isMenuOpen: false,
        };
    }

    componentDidMount() {
        if (!this.state.connectionEstablished || !this.state.connectionActive) {
            this.socket = io(Config.getInstance.apiUrl, {
                reconnection: !Helpers.isProd()
            });

            // Middleware to catch all incomming messages
            if (this.socket) {
                const onevent = (this.socket as any).onevent;
                (this.socket as any).onevent = function(packet) {
                    //console.log("onevent", packet);
                    const args = packet.data || [];
                    onevent.call(this, packet); // Original call
                    packet.data = ["*"].concat(args);
                    onevent.call(this, packet); // Additional call to catch-all
                };
            }

            // Handle connect
            this.socket.on("connect", () => {
                //console.log("connect");
                this.setState({ connectionActive: true });
            });

            // Handle disconnect
            this.socket.on("disconnect", () => {
                //console.log("disconnect");
                this.setState({ connectionActive: false });
            });
        }
    }

    setActiveEnv = (env) => {
        cookie.save("active-env", env);

        this.setState({activeEnv: env});
    }

    handleDrawerOpen = () => {
        this.setState({drawerOpen: true});
    }

    handleDrawerClose = () => {
        this.setState({drawerOpen: false});
    }

    routeProps = (routeProps) => ({
        ...routeProps,
        activeUser: this.props.activeUser,
        activeEnv: this.state.activeEnv,
        socket: this.socket,
        mode: this.props.mode,
        switchMode: this.props.switchMode
    });

    renderRoutes() {
        return Routes.reduce((items, route) => {
            if (route.access === null || this.props.activeUser.canAccessPage(route.access)) {
                items.push(<Route
                    key={route.path}
                    path={route.path}
                    exact={route.exact || false}
                    component={routeProps => <route.component
                        {...this.routeProps(routeProps)}
                        activeUser={this.props.activeUser}
                    />}
                />);
            }

            return items;
        }, [])
    }

    render() {
        return (
            <Router>
                {Helpers.redirectIfPossible() && <Redirect to={Helpers.redirectIfPossible() as any}/>}

                <div className="root">
                    <AppBarWrapped
                        setActiveEnv={this.setActiveEnv}
                        switchMode={this.props.switchMode}
                        mode={this.props.mode}
                        resetAppState={this.props.resetAppState}
                        {...this.routeProps({})}
                    />

                    <div className={`sidePaper ${this.props.mode}`}>
                        <SideMenu {...this.routeProps({})}/>
                    </div>

                    <main className="layoutContent" style={{ background: this.props.mode === "dark" ? "#1f1f1f" : "#f0f2f5" }}>
                        <Switch>
                            <Route
                                path="/"
                                exact={true}
                                component={routeProps => <Dashboard {...this.routeProps(routeProps)}/>}
                                key="dashboard-route"
                            />

                            {...this.renderRoutes()}

                            <Route component={Error404Page} />
                        </Switch>
                    </main>
                </div>
            </Router>
        );
    }
}
